import { Accordion, Badge, Divider, Grid, Group, Paper, Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'
import React from 'react'
import { CompanyItem } from './types'
import ReportItem from '../ReportItem'

interface CompanyCardProps {
  company: CompanyItem
}

const CompanyCard: React.FC<CompanyCardProps> = ({ company }) => {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return dayjs(dateStr).format('YYYY-MM-DD')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case '存续':
        return 'green'
      case '注销':
        return 'red'
      case '吊销':
        return 'orange'
      default:
        return 'gray'
    }
  }

  const getRelationshipText = (relationship: string) => {
    switch (relationship) {
      case 'sh':
        return '股东'
      case 'lp':
        return '法人'
      case 'tm':
        return '高管'
      default:
        return relationship
    }
  }

  const getRelationshipColor = (relationship: string) => {
    switch (relationship) {
      case 'sh':
        return 'purple'
      case 'lp':
        return 'blue'
      case 'tm':
        return 'cyan'
      default:
        return 'gray'
    }
  }

  const cardBackgroundColor = '#ffffff'

  return (
    <Paper
      p="md"
      style={{
        backgroundColor: cardBackgroundColor,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: '1px solid rgba(0,0,0,0.05)',
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs" style={{ flex: 1 }}>
            <Group gap="sm" align="center">
              <Text size="sm" fw={600} style={{ color: '#1c7ed6' }}>
                {company.orgName}
              </Text>
              <Badge color={getStatusColor(company.basicInfo.regStatus)} variant="light" size="sm">
                {company.basicInfo.regStatus}
              </Badge>
            </Group>

            <Group gap="xs">
              {company.relationship.map((rel, index) => (
                <Badge key={index} color={getRelationshipColor(rel)} variant="light" size="sm">
                  {getRelationshipText(rel)}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Group>

        <Grid gutter="xs">
          <Grid.Col span={6}>
            <Text size="xs" c="dimmed">
              统一社会信用代码
            </Text>
            <Text size="sm" fw={500}>
              {company.basicInfo.creditCode || '-'}
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text size="xs" c="dimmed">
              注册资本
            </Text>
            <Text size="sm" fw={500}>
              {company.basicInfo.regCapital || '-'}
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text size="xs" c="dimmed">
              成立日期
            </Text>
            <Text size="sm" fw={500}>
              {formatDate(company.basicInfo.estiblishTime)}
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <Text size="xs" c="dimmed">
              法定代表人
            </Text>
            <Text size="sm" fw={500}>
              {company.basicInfo.legalPersonName || '-'}
            </Text>
          </Grid.Col>
          {company.pName && company.pName !== '-' && (
            <Grid.Col span={12}>
              <Text size="xs" c="dimmed">
                查询人名称
              </Text>
              <Text size="sm" fw={500}>
                {company.pName}
              </Text>
            </Grid.Col>
          )}
        </Grid>

        {((company.dishonestExecutedPerson && company.dishonestExecutedPerson.length > 0) ||
          (company.executedPerson && company.executedPerson.length > 0) ||
          (company.adminPenalty && company.adminPenalty.length > 0)) && (
          <>
            <Divider my="sm" />
            <Accordion variant="separated">
              {company.dishonestExecutedPerson && company.dishonestExecutedPerson.length > 0 && (
                <Accordion.Item value="dishonest">
                  <Accordion.Control>
                    <Group gap="xs">
                      <Text size="sm" fw={600}>
                        失信被执行人
                      </Text>
                      <Badge color="red" variant="filled" size="sm">
                        {company.dishonestExecutedPerson.length}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {company.dishonestExecutedPerson.map((item, index) => (
                        <Paper
                          key={index}
                          p="xs"
                          style={{
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                          }}
                        >
                          <Stack gap="xs">
                            <ReportItem name="失信人名称" value={item.iname} />
                            <ReportItem name="案号" value={item.casecode} />
                            <ReportItem name="法院" value={item.courtname} />
                            <ReportItem name="立案时间" value={formatDate(item.regdate)} />
                            <ReportItem name="发布时间" value={formatDate(item.publishdate)} />
                            <ReportItem name="履行情况" value={item.performance} />
                            <ReportItem name="执行依据文号" value={item.gistid} />
                            <ReportItem name="生效法律文书确定的义务" value={item.duty} />
                            <ReportItem name="失信行为具体情形" value={item.disrupttypename} />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              )}

              {company.executedPerson && company.executedPerson.length > 0 && (
                <Accordion.Item value="executed">
                  <Accordion.Control>
                    <Group gap="xs">
                      <Text size="sm" fw={600}>
                        被执行人
                      </Text>
                      <Badge color="orange" variant="filled" size="sm">
                        {company.executedPerson.length}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {company.executedPerson.map((item, index) => (
                        <Paper
                          key={index}
                          p="xs"
                          style={{
                            backgroundColor: '#fff7ed',
                            border: '1px solid #fed7aa',
                          }}
                        >
                          <Stack gap="xs">
                            <ReportItem name="被执行人名称" value={item.pname} />
                            <ReportItem name="案号" value={item.caseCode} />
                            <ReportItem name="执行法院" value={item.execCourtName} />
                            <ReportItem name="立案日期" value={formatDate(item.caseCreateTime)} />
                            <ReportItem name="执行标的" value={item.execMoney ? `¥${item.execMoney}` : '-'} />
                            <ReportItem name="身份证号/组织机构代码" value={item.partyCardNum} />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              )}

              {company.adminPenalty && company.adminPenalty.length > 0 && (
                <Accordion.Item value="penalty">
                  <Accordion.Control>
                    <Group gap="xs">
                      <Text size="sm" fw={600}>
                        行政处罚
                      </Text>
                      <Badge color="yellow" variant="filled" size="sm">
                        {company.adminPenalty.length}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {company.adminPenalty.map((item, index) => (
                        <Paper
                          key={index}
                          p="xs"
                          style={{
                            backgroundColor: '#fefce8',
                            border: '1px solid #fde047',
                          }}
                        >
                          <Stack gap="xs">
                            <ReportItem name="处罚事由/违法行为类型" value={item.reason} />
                            <ReportItem name="决定文书号" value={item.punishNumber} />
                            <ReportItem name="处罚单位" value={item.departmentName} />
                            <ReportItem name="处罚日期" value={formatDate(item.decisionDate)} />
                            <ReportItem name="处罚结果/内容" value={item.content} />
                            <ReportItem name="法定代表人" value={item.legalPersonName} />
                            <ReportItem name="处罚类别" value={item.type} />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              )}
            </Accordion>
          </>
        )}
      </Stack>
    </Paper>
  )
}

export default CompanyCard
